class AccordionTab extends HTMLElement {
  constructor() {
    super();

    this.tabToggle = this.querySelector(".accordion__header");
    this.tabContent = this.querySelector(".accordion__content");

    this.getAttribute("aria-expanded") === "true"
      ? (this.tabContent.style.maxHeight = `${this.tabContent.scrollHeight + 50}px`)
      : (this.tabContent.style.maxHeight = `0px`);
  }

  connectedCallback() {
    this.tabToggle.addEventListener("click", () => this.toggleAccordion());
  }

  toggleAccordion() {
    const isExpanded = this.getAttribute("aria-expanded") === "true";

    if (!isExpanded) {
      // Expand accordion
      this.tabContent.style.maxHeight = `${this.tabContent.scrollHeight}px`;
      this.setAttribute("aria-expanded", "true");
    } else {
      // Collapse accordion
      this.tabContent.style.maxHeight = "0";
      this.setAttribute("aria-expanded", "false");
    }
  }
}

customElements.define("accordion-tab", AccordionTab);

class VATValidator extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.vatNumberInput = this.querySelector("input");
    this.validateButton = this.querySelector("button");
    this.messageContainer = this.querySelector(".addresses__vat-message");

    this.vatNumberInput.addEventListener("input", this.handleInput.bind(this));

    this.validateButton.addEventListener(
      "click",
      this.validateVATNumber.bind(this)
    );
  }

  handleInput() {
    this.vatNumberInput.value = this.vatNumberInput.value.toUpperCase();
  }

  validateVATNumber() {
    this.validateButton.classList.add("loading");
    this.validateButton.setAttribute("aria-disabled", true);
    this.validateButton
      .querySelector(".loading__spinner")
      .classList.remove("hidden");
    this.clearMessage();

    const vatNumber = this.vatNumberInput.value;

    fetch(`https://${window.location.host}/apps/course/validate-vat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ vat_number: vatNumber }),
    })
      .then(async (response) => {
        const data = await response.json();
        return { response, data };
      })
      .then(({ response, data }) => {
        this.validateButton.disabled = false;
        this.validateButton.classList.remove("button--loading");

        if (response.ok && data.success && data.valid) {
          // Success: VAT number is valid
          this.showMessage(data.message || "VAT number is valid", "success");
        } else {
          // Error: Validation failed
          const message = data.message || "VAT validation failed";
          this.showMessage(message, "error");
        }
      })
      .catch((error) => {
        console.error("Error validating VAT:", error);
        this.validateButton.classList.remove("loading");
        this.validateButton.removeAttribute("aria-disabled");
        this.validateButton
          .querySelector(".loading__spinner")
          .classList.add("hidden");

        this.showMessage(
          "An error occurred while validating the VAT number. Please try again.",
          "error"
        );
      });
  }

  showMessage(message, type) {
    this.messageContainer.innerHTML = `<p class="vat-message vat-message--${type}">${message}</p>`;
    this.messageContainer.classList.remove("hidden");
    setTimeout(() => {
      this.clearMessage();
    }, 5000);
  }

  clearMessage() {
    this.messageContainer.innerHTML = "";
    this.messageContainer.classList.add("hidden");
  }
}

customElements.define("vat-validator", VATValidator);

class CustomerModules extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.modulesContainer = this.querySelector(".modules");
    this.modulesGrid = this.modulesContainer.querySelector(".product-grid");
    this.emptyState = this.querySelector(".empty-state");

    this.handleModules();
  }

  handleModules() {
    fetch(`https://${window.location.host}/apps/course/modules`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        const { courses, purchased_products, course_product_map } = data;

        if (courses.length === 0) {
          this.modulesContainer.remove();
          this.emptyState.removeAttribute("hidden");
          return;
        }

        fetch(
          `${window.Shopify.routes.root}collections/modules?section_id=module-grid`
        )
          .then((response) => {
            return response.text();
          })
          .then((html) => {
            const tempDiv = document.createElement("div");
            const productIds = purchased_products.map((product) => {
              return product.split("/").pop();
            });
            tempDiv.innerHTML = html;

            const validModules = Array.from(
              tempDiv.querySelectorAll(".grid__item")
            ).filter((item) => {
              return productIds.includes(item.id);
            });

            if (validModules.length === 0) {
              this.modulesContainer.remove();
              this.emptyState.removeAttribute("hidden");
              return;
            }

            this.modulesGrid.innerHTML = "";

            validModules.forEach((module) => {
              const productId = module.id;
              const currentCourse = courses.find((course) => {
                return (
                  course_product_map[`gid://shopify/Product/${productId}`]
                    .courseId === course.course.id
                );
              });

              const progressDisplay = module.querySelector("progress-display");
              const progressText = module.querySelector(
                ".progress-display__completion-text"
              );
              const progressDisplayBar = module.querySelector(
                ".progress-display__bar-progress"
              );
              const currentLessonLink = module.querySelectorAll('[data-link]');
              progressDisplay.style.setProperty(
                "--progress",
                `${currentCourse.completedRatio}%`
              );
              progressText.textContent = `${currentCourse.completedRatio}% Completed`;
              
              if (currentCourse.completedRatio < 100) {
                progressDisplayBar.classList.add('progress-display__bar-progress--remaining')
              } else {
                progressDisplayBar.classList.add('progress-display__bar-progress--achieved')
              }
              currentLessonLink.forEach((link) => {
                link.href = currentCourse.currentLessonUrl;
              });

              this.modulesGrid.appendChild(module);
            });
          });
      });
  }
}

customElements.define("customer-modules", CustomerModules);

class CountdownDisplay extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.countdownDate = Number(this.dataset.date) * 1000;
    this.daysElement = this.querySelector("[data-countdown-days]");
    this.hoursElement = this.querySelector("[data-countdown-hours]");
    this.minutesElement = this.querySelector("[data-countdown-minutes]");
    this.secondsElement = this.querySelector("[data-countdown-seconds]");

    if (!this.daysElement) return;

    this.init();
  }

  init() {
    this.updateCountdown();
    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  updateCountdown() {
    const now = new Date().getTime();
    const timeDifference = this.countdownDate - now;

    if (timeDifference < 0) {
      clearInterval(this.countdownInterval);

      const announcementCountdown = this.closest(".announcement-bar__countdown");
      if (announcementCountdown) {
        announcementCountdown.remove();
        return;
      }

      const countdownBar = this.closest(".countdown-bar");
      if (countdownBar) {
        countdownBar.closest(".utility-bar")?.remove();
      }

      return;
    }

    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor(
      (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
    );
    const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

    this.daysElement.innerText = this.formatTime(days);
    this.hoursElement.innerText = this.formatTime(hours);
    this.minutesElement.innerText = this.formatTime(minutes);
    this.secondsElement.innerText = this.formatTime(seconds);
  }

  formatTime(timeUnit) {
    return timeUnit < 10 ? "0" + timeUnit : timeUnit;
  }
}

customElements.define("countdown-display", CountdownDisplay);
